import { useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useNavigate, useParams } from "react-router-dom"
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from "recharts"
import { ChevronDown, ChevronUp, MessageSquare } from "lucide-react"

const COLORS = ["#0ea5e9", "#8b5cf6", "#f97316", "#10b981", "#ec4899"]

interface SurveyQuestion {
  id: string
  question: string
  type: "rating" | "choice" | "text"
  responses: number
  options?: { label: string; votes: number }[]
  ratingAvg?: number
  textResponses?: string[]
}

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-xl shadow-lg p-3 text-xs">
      <p className="font-semibold text-slate-800 dark:text-white mb-1">{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ color: p.fill }} className="mt-0.5">{p.name}: <strong>{p.value}</strong></p>
      ))}
    </div>
  )
}

export default function FeedbackSurvey() {
  const t = useTranslation()
  const navigate = useNavigate()
  const { id }   = useParams()
  const [open, setOpen] = useState<string[]>(["q1"])

  const mockSurvey: SurveyQuestion[] = [
    {
      id: "q1",
      question: "How relevant was the webinar content to your needs?",
      type: "choice",
      responses: 64,
      options: [
        { label: "Very Relevant", votes: 42 },
        { label: "Somewhat Relevant", votes: 18 },
        { label: "Not Relevant", votes: 4 },
      ],
      textResponses: [
        "The content was very practical and actionable.",
        "Great insights on the new features.",
      ],
    },
    {
      id: "s2",
      question: "Would you attend another webinar from us?",
      type: "choice",
      responses: 54,
      options: [
        { label: "Definitely", votes: 38 },
        { label: "Maybe", votes: 14 },
        { label: "Probably Not", votes: 2 },
      ],
      textResponses: [
        "Looking forward to the next one!",
        "Please cover more advanced topics.",
      ],
    },
  ]

  const toggleQ = (qid: string) =>
    setOpen(prev => prev.includes(qid) ? prev.filter(x => x !== qid) : [...prev, qid])

  return (
    <div className="flex flex-col h-full w-full overflow-y-auto bg-background p-4 md:p-6 gap-5">

      {/* Breadcrumb + Header */}
      <div>
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-3">
          <button onClick={() => navigate("/webinar")} className="text-primary font-semibold hover:text-primary transition-colors">{t("webinar.webinar")}</button>
          <span>/</span>
          <button onClick={() => navigate(`/webinar/${id}/analytics`)} className="text-primary font-semibold hover:text-primary transition-colors">{t("webinar.analytics")}</button>
          <span>/</span>
          <span className="text-slate-700 dark:text-slate-200 font-semibold">{t("webinar.feedbackSurveyTitle")}</span>
        </div>

        <div className="flex items-center gap-3">
          {/* <button
            onClick={() => navigate(`/webinar/${id}/analytics`)}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 text-slate-600 dark:text-slate-400" />
          </button> */}
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{t("webinar.feedbackSurveyTitle")}</h1>
            <p className="text-xs text-slate-400 mt-0.5">
              {mockSurvey.reduce((s, q) => s + q.responses, 0)} {t("webinar.totalResponsesAcross")} {mockSurvey.length} {t("webinar.questions")}
            </p>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          { label: t("webinar.totalResponses"),  value: "42", iconBg: "bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20" },
          { label: t("webinar.avgRating"),      value: "4.3 / 5", iconBg: "bg-gradient-to-br from-amber-50 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20" },
          { label: t("webinar.wouldRecommend"),  value: "87%", iconBg: "bg-gradient-to-br from-emerald-50 to-teal-100 dark:from-emerald-900/20 dark:to-teal-900/20" },
        ].map(s => (
          <div key={s.label} className="group relative bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-2xl px-4 py-4 shadow-xs overflow-hidden text-left">
            <div className={`absolute inset-0 ${s.iconBg}`}>
              <svg className="absolute bottom-0 right-0 w-full h-full" viewBox="0 0 200 120" fill="none" preserveAspectRatio="none">
                <path d="M200 120V80C180 70 160 75 140 80C120 85 100 90 80 85C60 80 40 70 20 75C10 77.5 5 79 0 80V120H200Z" fill="currentColor" className="text-white/30 dark:text-white/10" />
              </svg>
            </div>
            <div className="relative z-10">
              <div className="text-xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white mb-1">{s.value}</div>
              <div className="text-sm font-medium text-slate-600 dark:text-slate-400">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Questions */}
      <div className="space-y-3">
        {mockSurvey.map((q, idx) => (
          <div key={q.id} className="bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-2xl overflow-hidden shadow-sm">
            <button
              onClick={() => toggleQ(q.id)}
              className="w-full flex items-center gap-3 px-5 py-4 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors text-left"
            >
              <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-white text-xs font-bold shrink-0">
                Q{idx + 1}
              </div>
              <span className="flex-1 text-sm font-semibold text-slate-800 dark:text-slate-200">{q.question}</span>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-xs text-slate-400">{q.responses} {t("webinar.responsesLabel")}</span>
                {open.includes(q.id)
                  ? <ChevronUp   className="w-4 h-4 text-slate-400" />
                  : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </div>
            </button>

            {open.includes(q.id) && (
              <div className="px-5 pb-5 border-t border-stroke-100 dark:border-border dark:border-border">
                {q.type === "text" ? (
                  <div className="mt-4 space-y-2">
                    {q.textResponses?.map((r, i) => (
                      <div key={i} className="flex items-start gap-2.5 p-3 bg-slate-50 dark:bg-slate-700/30 rounded-xl">
                        <MessageSquare className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                        <p className="text-sm text-slate-700 dark:text-slate-300">{r}</p>
                      </div>
                    ))}
                    {q.responses > (q.textResponses?.length ?? 0) && (
                      <p className="text-xs text-slate-400 text-center mt-2">
                        +{q.responses - (q.textResponses?.length ?? 0)} {t("webinar.moreResponses")}
                      </p>
                    )}
                  </div>
                ) : (
                  <>
                    {q.ratingAvg && (
                      <div className="mt-4 mb-2 flex items-center gap-2">
                        <span className="text-2xl font-black text-primary">{q.ratingAvg}</span>
                        <span className="text-sm text-slate-400">/ 5 {t("webinar.averageRating")}</span>
                      </div>
                    )}
                    <div className="h-[150px] mt-3">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={q.options} margin={{ top: 8, right: 8, left: -20, bottom: 0 }} barSize={32}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
                          <XAxis dataKey="label" fontSize={10} stroke="currentColor" />
                          <YAxis fontSize={10} stroke="currentColor" allowDecimals={false} />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar dataKey="votes" name="Votes" radius={[4, 4, 0, 0]}>
                            {q.options?.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex flex-wrap gap-3 mt-2">
                      {q.options?.map((o, i) => (
                        <div key={o.label} className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            {o.label}: <strong className="text-slate-800 dark:text-white">{o.votes}</strong>
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
